// src/components/Header.tsx
import React, { memo, useCallback } from 'react';
import { AppBar, Toolbar, IconButton, Typography, Box, Button, Avatar, Stack } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';

interface HeaderProps {
  drawerWidth: number;
  handleDrawerToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ drawerWidth, handleDrawerToggle }) => {
  const { authState, logout } = useAuth();
  const { t } = useTranslation();

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        backgroundColor: 'white',
        borderBottom: '1px solid #e2e8f0',
      }}
    >
      <Toolbar sx={{ minHeight: 64 }}>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{ mr: 2, display: { sm: 'none' }, color: '#64748b' }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: '#1e293b', fontWeight: 600 }}>
          {t('header.brand')}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#2563eb',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {authState.user?.name?.charAt(0).toUpperCase() || 'A'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Typography variant="body2" fontWeight={600} color="#1e293b">
                {authState.user?.name || 'Admin'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {authState.user?.role || 'User'}
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            size="small"
            startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
            onClick={handleLogout}
            sx={{
              borderRadius: 1,
              borderColor: '#e2e8f0',
              color: '#64748b',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#dc2626',
                color: '#dc2626',
                bgcolor: '#fef2f2',
              },
            }}
          >
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              {t('header.logout')}
            </Box>
          </Button>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default memo(Header);
